/* App-wide user preferences (theme, time zone, time format, compact
 * mode, default landing page) persisted to localStorage.
 *
 * Single key: `nt.settings.v1`. Rendered via a Provider so any
 * component can read + mutate. Falls back to safe defaults when
 * localStorage is unavailable (SSR / privacy mode / etc).
 */

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'

const STORAGE_KEY = 'nt.settings.v1'

const detectTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

const DEFAULTS = {
  theme: 'system',          // 'light' | 'dark' | 'system'
  timeZone: null,           // null = resolve on first read
  timeFormat: '12h',        // '12h' | '24h'
  compact: false,
  defaultLandingPage: '/',  // one of the six main routes
}

const readStoredSettings = () => {
  if (typeof window === 'undefined') return { ...DEFAULTS, timeZone: detectTimeZone() }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULTS, timeZone: detectTimeZone() }
    const parsed = JSON.parse(raw)
    return {
      ...DEFAULTS,
      ...parsed,
      timeZone: parsed.timeZone || detectTimeZone(),
    }
  } catch {
    return { ...DEFAULTS, timeZone: detectTimeZone() }
  }
}

const AppSettingsContext = createContext({
  settings: { ...DEFAULTS, timeZone: 'UTC' },
  updateSetting: () => {},
  resetSettings: () => {},
  autoTimeZone: 'UTC',
})

export function AppSettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => readStoredSettings())
  const autoTimeZone = useMemo(() => detectTimeZone(), [])

  // Persist whenever settings change.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
    } catch {
      /* best-effort */
    }
  }, [settings])

  // Apply theme — writes data-theme on <html>. 'system' listens to OS.
  useEffect(() => {
    const root = document.documentElement
    const apply = (mode) => {
      root.setAttribute('data-theme', mode === 'dark' ? 'dark' : 'light')
    }
    if (settings.theme === 'light' || settings.theme === 'dark') {
      apply(settings.theme)
      return
    }
    // system
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    apply(mq.matches ? 'dark' : 'light')
    const handler = (e) => apply(e.matches ? 'dark' : 'light')
    try {
      mq.addEventListener('change', handler)
      return () => mq.removeEventListener('change', handler)
    } catch {
      // Safari <14 fallback
      mq.addListener(handler)
      return () => mq.removeListener(handler)
    }
  }, [settings.theme])

  // Apply compact mode.
  useEffect(() => {
    const root = document.documentElement
    if (settings.compact) {
      root.setAttribute('data-compact', 'true')
    } else {
      root.removeAttribute('data-compact')
    }
  }, [settings.compact])

  const updateSetting = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }, [])

  const resetSettings = useCallback(() => {
    setSettings({ ...DEFAULTS, timeZone: detectTimeZone() })
  }, [])

  const value = useMemo(
    () => ({ settings, updateSetting, resetSettings, autoTimeZone }),
    [settings, updateSetting, resetSettings, autoTimeZone]
  )

  return (
    <AppSettingsContext.Provider value={value}>
      {children}
    </AppSettingsContext.Provider>
  )
}

export function useAppSettings() {
  return useContext(AppSettingsContext)
}

// Non-hook accessor — useful inside helpers that don't live in React.
export function readAppSettingsSnapshot() {
  return readStoredSettings()
}

export const COMMON_TIME_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
]

export const LANDING_PAGES = [
  { value: '/', label: 'Home' },
  { value: '/discussions', label: 'Discussions' },
  { value: '/announcements', label: 'Announcements' },
  { value: '/events', label: 'Events' },
  { value: '/directory', label: 'Directory' },
  { value: '/messages', label: 'Messages' },
]
